import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Tooltip,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { formatLocalizedDateTime } from '../helpers/date-locale.helper';
import PageHeader from '../components/PageHeader';
import { useNotificationStore } from '../stores/notification.store';
import { useOrganizationStore } from '../stores/organization.store';
import { Notification } from '../types/notification.type';

const Notifications: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { selectedOrganization } = useOrganizationStore();
  const organizationId = selectedOrganization?.id ?? null;
  const [tab, setTab] = useState<number>(0);
  const [markingAllAsRead, setMarkingAllAsRead] = useState<boolean>(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    updateUnreadCount,
  } = useNotificationStore();

  const unreadNotifications = useMemo(() => {
    try {
      return notifications.filter((n) => n && !n.is_read && !removingIds.has(n.id));
    } catch (error) {
      console.error('Error filtering notifications:', error);
      return [];
    }
  }, [notifications, removingIds]);

  const readNotifications = useMemo(() => {
    try {
      return notifications.filter((n) => n && n.is_read && !removingIds.has(n.id));
    } catch (error) {
      console.error('Error filtering notifications:', error);
      return [];
    }
  }, [notifications, removingIds]);

  const displayedNotifications = tab === 0 ? unreadNotifications : readNotifications;

  useEffect(() => {
    if (!organizationId) return;
    fetchNotifications(organizationId, { limit: 20 });
    const interval = setInterval(() => {
      fetchNotifications(organizationId, { limit: 20 });
    }, 30000);
    return () => clearInterval(interval);
  }, [organizationId, fetchNotifications]);

  useEffect(() => {
    updateUnreadCount();
  }, [notifications, updateUnreadCount]);

  const handleMarkAsRead = async (notificationId: string) => {
    if (organizationId) {
      setRemovingIds((prev) => new Set(prev).add(notificationId));
      await markAsRead(organizationId, notificationId);
      setTimeout(() => {
        setRemovingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(notificationId);
          return newSet;
        });
      }, 300);
    }
  };

  const handleNotificationItemClick = async (notification: Notification) => {
    if (organizationId && !notification.is_read) {
      await handleMarkAsRead(notification.id);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (organizationId && notifications.length > 0) {
      setMarkingAllAsRead(true);
      try {
        const unread = notifications.filter((n) => !n.is_read);
        for (const notification of unread) {
          await markAsRead(organizationId, notification.id);
        }
      } finally {
        setMarkingAllAsRead(false);
      }
    }
  };

  const getNotificationText = (notification: Notification) => {
    try {
      const isArabic = i18n.language.startsWith('ar');
      return {
        title: isArabic
          ? notification.title_ar || notification.title_en
          : notification.title_en || notification.title_ar,
        message: isArabic
          ? notification.message_ar || notification.message_en
          : notification.message_en || notification.message_ar,
      };
    } catch (error) {
      console.error('Error getting notification text:', error);
      return {
        title: notification.title_en || notification.title_ar || 'Notification',
        message: notification.message_en || notification.message_ar || '',
      };
    }
  };

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return t('common.unknown', { defaultValue: 'Unknown' });
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return formatLocalizedDateTime(date, i18n.language);
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('common.unknown', { defaultValue: 'Unknown' });
    }
  };

  return (
    <Box>
      <PageHeader
        icon={<NotificationsIcon />}
        title={t('notifications.title')}
        subtitle={unreadCount > 0 ? t('notifications.unreadCount', { count: unreadCount }) : undefined}
        action={
          tab === 0 && unreadNotifications.length > 0 ? (
            <Button
              variant="outlined"
              onClick={handleMarkAllAsRead}
              disabled={markingAllAsRead}
              startIcon={
                markingAllAsRead ? <CircularProgress size={16} /> : <CheckCircleIcon />
              }
              sx={{ borderRadius: '12px' }}
            >
              {markingAllAsRead ? t('notifications.marking') : t('notifications.markAllAsRead')}
            </Button>
          ) : undefined
        }
        footer={
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ minHeight: 0, '& .MuiTab-root': { minHeight: 40 } }}
          >
            <Tab label={t('notifications.tabUnread')} />
            <Tab label={t('notifications.tabRead')} />
          </Tabs>
        }
      />

      <Paper elevation={2} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        {isLoading && displayedNotifications.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : displayedNotifications.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {tab === 0 ? t('notifications.emptyUnread') : t('notifications.emptyRead')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {displayedNotifications.map((notification, index) => {
              const { title, message } = getNotificationText(notification);
              const isUnread = !notification.is_read;

              return (
                <React.Fragment key={notification.id}>
                  <ListItem
                    onClick={() => handleNotificationItemClick(notification)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isUnread ? 'action.hover' : 'transparent',
                      transition: 'background-color 0.2s',
                      '&:hover': { bgcolor: 'action.selected' },
                      py: 2,
                      px: 3,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: isUnread ? 600 : 400 }}
                        >
                          {title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(notification.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip
                        title={
                          isUnread
                            ? t('notifications.markAsRead')
                            : t('notifications.markedAsRead')
                        }
                      >
                        <span>
                          <IconButton
                            edge="end"
                            size="small"
                            disabled={!isUnread}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (isUnread) void handleMarkAsRead(notification.id);
                            }}
                            sx={{ color: isUnread ? 'primary.main' : 'action.disabled' }}
                          >
                            {isUnread ? (
                              <CheckCircleOutlineIcon fontSize="small" />
                            ) : (
                              <CheckCircleIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < displayedNotifications.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default Notifications;
